/**
 * SnappingConfigPanel Component
 *
 * UI panel for configuring snapping behavior during digitization:
 * - Enable/disable snapping
 * - Tolerance slider (pixel distance)
 * - Snap to vertices toggle
 * - Snap to edges toggle
 * - Snap to intersections toggle
 *
 * Validates: Requirements 48.6
 */
import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  Slider,
  Typography,
  Stack,
  Box,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  setSnappingConfig,
  toggleSnapping,
  setSnappingTolerance,
  toggleSnapToVertices,
  toggleSnapToEdges,
  toggleSnapToIntersections,
  setSnappingConfigDialogOpen,
} from '../store/slices/digitizationSlice';
import type { SnappingConfig } from '../types/layerCreation';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SnappingConfigPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const snapping = useAppSelector((state) => state.digitization.snapping);
  const open = useAppSelector((state) => state.digitization.snappingConfigDialogOpen);

  const handleClose = useCallback(() => {
    dispatch(setSnappingConfigDialogOpen(false));
  }, [dispatch]);

  const handleToggleEnabled = useCallback(() => {
    dispatch(toggleSnapping());
  }, [dispatch]);

  const handleToleranceChange = useCallback(
    (_: Event, value: number | number[]) => {
      dispatch(setSnappingTolerance(value as number));
    },
    [dispatch]
  );

  const handleToggleVertices = useCallback(() => {
    dispatch(toggleSnapToVertices());
  }, [dispatch]);

  const handleToggleEdges = useCallback(() => {
    dispatch(toggleSnapToEdges());
  }, [dispatch]);

  const handleToggleIntersections = useCallback(() => {
    dispatch(toggleSnapToIntersections());
  }, [dispatch]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Snapping Configuration</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch checked={snapping.enabled} onChange={handleToggleEnabled} color="primary" />
            }
            label="Enable Snapping"
          />

          <Box sx={{ px: 1 }}>
            <Typography variant="body2" gutterBottom>
              Tolerance: {snapping.tolerance}px
            </Typography>
            <Slider
              value={snapping.tolerance}
              onChange={handleToleranceChange}
              min={1}
              max={30}
              step={1}
              disabled={!snapping.enabled}
              marks={[
                { value: 1, label: '1' },
                { value: 10, label: '10' },
                { value: 20, label: '20' },
                { value: 30, label: '30' },
              ]}
              aria-label="Snapping tolerance"
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Snap Targets
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={snapping.snapToVertices}
                onChange={handleToggleVertices}
                disabled={!snapping.enabled}
                size="small"
              />
            }
            label="Snap to Vertices"
          />

          <FormControlLabel
            control={
              <Switch
                checked={snapping.snapToEdges}
                onChange={handleToggleEdges}
                disabled={!snapping.enabled}
                size="small"
              />
            }
            label="Snap to Edges"
          />

          <FormControlLabel
            control={
              <Switch
                checked={snapping.snapToIntersections}
                onChange={handleToggleIntersections}
                disabled={!snapping.enabled}
                size="small"
              />
            }
            label="Snap to Intersections"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SnappingConfigPanel;
